/**
 * @fileoverview UserPool client for interacting with the userpool service.
 * This file implements the client that makes authentication and user management
 * requests to the userpool service with built-in retry logic.
 *
 * Key Design Principles:
 * - NO CACHING: All operations are performed fresh (Workers requirement)
 * - FAIL-SECURE: Returns errors appropriately
 * - RETRY PATTERN: Exponential backoff with jitter for transient failures
 */

import { ErrorAuthentication, ErrorAuthorization } from "@eoln/errors";
import { HttpServerError, NetworkError, RetryManager, type RetryMetrics } from "@eoln/resilience";
import type {
  CreateUserInput,
  LoginResponse,
  PoolSettings,
  UpdatePoolSettingsInput,
  UpdateUserInput,
  User,
  UserConnection,
  UserSession,
} from "@eoln/userpool/types";
import type { ListUsersOptions } from "../types/user.ts";

/**
 * GraphQL error structure returned by the server
 */
interface GraphQLError {
  message: string;
  extensions?: {
    code?: string;
    requiredScopes?: string[];
  };
}

/**
 * Generic GraphQL response wrapper
 */
interface GraphQLResponse<T = unknown> {
  data?: T;
  errors?: GraphQLError[];
}

/**
 * Configuration options for the UserPoolClient.
 */
export interface UserPoolClientOptions {
  /**
   * URL of the GraphQL API endpoint for the userpool service.
   */
  endpoint: string;

  /**
   * API key for authentication (admin API key or session API key).
   */
  apiKey?: string;

  /**
   * Retry configuration for transient failures.
   */
  retry?: {
    maxAttempts?: number;
    initialDelayMs?: number;
    maxDelayMs?: number;
    backoffMultiplier?: number;
    jitter?: boolean;
  };
}

/**
 * Client for interacting with the UserPool service.
 *
 * This class provides methods for:
 * - User authentication (login, logout, register)
 * - Session management (me, mySessions, refreshSession)
 * - User management (CRUD operations)
 * - Pool settings management
 *
 * @example
 * ```typescript
 * const client = new UserPoolClient({
 *   endpoint: 'https://userpool.api.example.com/graphql',
 *   apiKey: 'sk_admin_...'
 * });
 *
 * // Login a user
 * const { apiKey, user } = await client.login('user@example.com', 'password');
 *
 * // Use the session API key for user operations
 * client.setApiKey(apiKey);
 * const me = await client.me();
 * ```
 */
export class UserPoolClient {
  private readonly endpoint: string;
  private apiKey: string | undefined;
  private readonly retryManager: RetryManager;

  /**
   * Creates a new UserPoolClient instance.
   *
   * @param options - Configuration options
   */
  constructor(options: UserPoolClientOptions) {
    this.endpoint = options.endpoint;
    this.apiKey = options.apiKey;
    this.retryManager = new RetryManager({
      maxAttempts: options.retry?.maxAttempts ?? 3,
      initialDelayMs: options.retry?.initialDelayMs ?? 100,
      maxDelayMs: options.retry?.maxDelayMs ?? 2000,
      backoffMultiplier: options.retry?.backoffMultiplier ?? 2,
      jitter: options.retry?.jitter ?? true,
      name: "UserPoolClient",
    });
  }

  /**
   * Sets the API key for this client instance.
   *
   * @param apiKey - The API key to use for authentication
   */
  setApiKey(apiKey: string): void {
    this.apiKey = apiKey;
  }

  /**
   * Gets the currently configured API key for this client instance.
   *
   * @returns The current API key or undefined if not set
   */
  getConfiguredApiKey(): string | undefined {
    return this.apiKey;
  }

  /**
   * Get health metrics for monitoring retry behavior.
   */
  getHealthMetrics(): { retry: RetryMetrics } {
    return {
      retry: this.retryManager.getMetrics(),
    };
  }

  // ============================================
  // Authentication (no auth required)
  // ============================================

  /**
   * Login with email and password.
   *
   * @param email - User's email address
   * @param password - User's password
   * @returns Login response with API key, user, and session
   * @throws {Error} If login fails
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const query = `
      mutation Login($email: String!, $password: String!) {
        login(email: $email, password: $password) {
          apiKey
          expiresAt
          user {
            userId email emailVerified fullName status statusReason
            createdAt updatedAt lastLoginAt
          }
          session {
            sessionId userAgent ipAddress expiresAt createdAt
          }
        }
      }
    `;

    const response = await this.graphqlRequest<{ login: LoginResponse }>(query, {
      email,
      password,
    });

    return response.login;
  }

  /**
   * Register a new user (self-registration).
   *
   * @param input - User creation input
   * @returns Created user
   * @throws {Error} If registration fails or is disabled
   */
  async register(input: CreateUserInput): Promise<User> {
    const query = `
      mutation Register($input: CreateUserInput!) {
        register(input: $input) {
          userId email emailVerified fullName status statusReason
          createdAt updatedAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ register: User }>(query, { input });
    return response.register;
  }

  // ============================================
  // User Operations (@auth - session key)
  // ============================================

  /**
   * Get current authenticated user.
   *
   * @returns Current user
   * @throws {ErrorAuthentication} If not authenticated
   */
  async me(): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query Me {
        me {
          userId email emailVerified fullName status statusReason
          metadata createdAt updatedAt lastLoginAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ me: User }>(query);
    return response.me;
  }

  /**
   * Get current user's active sessions.
   *
   * @returns List of active sessions
   * @throws {ErrorAuthentication} If not authenticated
   */
  async mySessions(): Promise<UserSession[]> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query MySessions {
        mySessions {
          sessionId userAgent ipAddress expiresAt createdAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ mySessions: UserSession[] }>(query);
    return response.mySessions;
  }

  /**
   * Logout current session.
   *
   * @returns True if logout successful
   * @throws {ErrorAuthentication} If not authenticated
   */
  async logout(): Promise<boolean> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation Logout {
        logout
      }
    `;

    const response = await this.graphqlRequest<{ logout: boolean }>(query);
    return response.logout;
  }

  /**
   * Refresh the current session. The server ROTATES the credential and
   * revokes the previous key, so this client adopts the new one.
   *
   * @returns New login response with the rotated key and extended session
   * @throws {ErrorAuthentication} If not authenticated
   */
  async refreshSession(): Promise<LoginResponse> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation RefreshSession {
        refreshSession {
          apiKey
          expiresAt
          user {
            userId email emailVerified fullName status statusReason
            createdAt updatedAt lastLoginAt
          }
          session {
            sessionId userAgent ipAddress expiresAt createdAt
          }
        }
      }
    `;

    const response = await this.graphqlRequest<{ refreshSession: LoginResponse }>(query);
    this.setApiKey(response.refreshSession.apiKey);
    return response.refreshSession;
  }

  /**
   * Change password for current user.
   *
   * @param currentPassword - Current password
   * @param newPassword - New password
   * @returns True if password changed successfully
   * @throws {ErrorAuthentication} If not authenticated
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation ChangePassword($currentPassword: String!, $newPassword: String!) {
        changePassword(currentPassword: $currentPassword, newPassword: $newPassword)
      }
    `;

    const response = await this.graphqlRequest<{ changePassword: boolean }>(query, {
      currentPassword,
      newPassword,
    });

    return response.changePassword;
  }

  /**
   * Resend verification email to current user.
   *
   * @returns True if email sent successfully
   * @throws {ErrorAuthentication} If not authenticated
   */
  async resendVerificationEmail(): Promise<boolean> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation ResendVerificationEmail {
        resendVerificationEmail
      }
    `;

    const response = await this.graphqlRequest<{ resendVerificationEmail: boolean }>(query);
    return response.resendVerificationEmail;
  }

  /**
   * Request password reset email.
   * Sends a password reset link to the user's email address.
   *
   * @param email - User's email address
   * @returns True if email sent (always returns true to prevent email enumeration)
   */
  async requestPasswordReset(email: string): Promise<boolean> {
    const query = `
      mutation RequestPasswordReset($email: String!) {
        requestPasswordReset(email: $email)
      }
    `;

    const response = await this.graphqlRequest<{ requestPasswordReset: boolean }>(query, { email });
    return response.requestPasswordReset;
  }

  /**
   * Reset password using token from email.
   * Completes the password reset flow initiated by requestPasswordReset.
   *
   * @param token - Reset token from email
   * @param newPassword - New password to set
   * @returns True if password reset successful
   */
  async resetPassword(token: string, newPassword: string): Promise<boolean> {
    const query = `
      mutation ResetPassword($token: String!, $newPassword: String!) {
        resetPassword(token: $token, newPassword: $newPassword)
      }
    `;

    const response = await this.graphqlRequest<{ resetPassword: boolean }>(query, { token, newPassword });
    return response.resetPassword;
  }

  /**
   * Verify email using token from verification email.
   * Activates the user account if email verification was required.
   *
   * @param token - Verification token from email
   * @returns True if email verified successfully
   */
  async verifyEmail(token: string): Promise<boolean> {
    const query = `
      mutation VerifyEmail($token: String!) {
        verifyEmail(token: $token)
      }
    `;

    const response = await this.graphqlRequest<{ verifyEmail: boolean }>(query, { token });
    return response.verifyEmail;
  }

  // ============================================
  // Admin Queries (@requireScope userpool:read)
  // ============================================

  /**
   * Get user by ID (admin).
   *
   * @param userId - User ID
   * @returns User or null if not found
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async getUser(userId: string): Promise<User | null> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query GetUser($userId: ID!) {
        user(userId: $userId) {
          userId email emailVerified fullName status statusReason
          metadata createdAt updatedAt lastLoginAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ user: User | null }>(query, { userId });
    return response.user;
  }

  /**
   * List users with pagination (admin).
   *
   * @param options - List options (pagination, status filter)
   * @returns Paginated user list
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async listUsers(options?: ListUsersOptions): Promise<UserConnection> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query ListUsers($pagination: PaginationInput, $status: UserStatus) {
        users(pagination: $pagination, status: $status) {
          nodes {
            userId email emailVerified fullName status statusReason
            metadata createdAt updatedAt lastLoginAt
          }
          totalCount
          hasMore
        }
      }
    `;

    const response = await this.graphqlRequest<{ users: UserConnection }>(query, {
      pagination: {
        limit: options?.pagination?.limit ?? 50,
        offset: options?.pagination?.offset ?? 0,
      },
      status: options?.status,
    });

    return response.users;
  }

  // ============================================
  // Admin Mutations (@requireScope userpool:admin)
  // ============================================

  /**
   * Create a new user (admin).
   *
   * @param input - User creation input
   * @param skipVerification - Skip email verification (default: false)
   * @returns Created user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async createUser(input: CreateUserInput, skipVerification = false): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation CreateUser($input: CreateUserInput!, $skipVerification: Boolean) {
        createUser(input: $input, skipVerification: $skipVerification) {
          userId email emailVerified fullName status statusReason
          createdAt updatedAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ createUser: User }>(query, {
      input,
      skipVerification,
    });

    return response.createUser;
  }

  /**
   * Update user details (admin).
   *
   * @param userId - User ID
   * @param input - Update input
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async updateUser(userId: string, input: UpdateUserInput): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation UpdateUser($userId: ID!, $input: UpdateUserInput!) {
        updateUser(userId: $userId, input: $input) {
          userId email emailVerified fullName status statusReason
          metadata createdAt updatedAt
        }
      }
    `;

    const response = await this.graphqlRequest<{ updateUser: User }>(query, {
      userId,
      input,
    });

    return response.updateUser;
  }

  /**
   * Delete user (admin).
   *
   * @param userId - User ID
   * @returns True if deleted successfully
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async deleteUser(userId: string): Promise<boolean> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation DeleteUser($userId: ID!) {
        deleteUser(userId: $userId)
      }
    `;

    const response = await this.graphqlRequest<{ deleteUser: boolean }>(query, { userId });
    return response.deleteUser;
  }

  // ============================================
  // User Lifecycle (admin)
  // ============================================

  /**
   * Activate a pending user (admin).
   *
   * @param userId - User ID
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async activateUser(userId: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation ActivateUser($userId: ID!) {
        activateUser(userId: $userId) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ activateUser: User }>(query, { userId });
    return response.activateUser;
  }

  /**
   * Suspend a user (admin).
   *
   * @param userId - User ID
   * @param reason - Optional suspension reason
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async suspendUser(userId: string, reason?: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation SuspendUser($userId: ID!, $reason: String) {
        suspendUser(userId: $userId, reason: $reason) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ suspendUser: User }>(query, {
      userId,
      reason,
    });

    return response.suspendUser;
  }

  /**
   * Unsuspend a user (admin).
   *
   * @param userId - User ID
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async unsuspendUser(userId: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation UnsuspendUser($userId: ID!) {
        unsuspendUser(userId: $userId) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ unsuspendUser: User }>(query, { userId });
    return response.unsuspendUser;
  }

  /**
   * Disable a user (admin).
   *
   * @param userId - User ID
   * @param reason - Optional disable reason
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async disableUser(userId: string, reason?: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation DisableUser($userId: ID!, $reason: String) {
        disableUser(userId: $userId, reason: $reason) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ disableUser: User }>(query, {
      userId,
      reason,
    });

    return response.disableUser;
  }

  /**
   * Enable a disabled user (admin).
   *
   * @param userId - User ID
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async enableUser(userId: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation EnableUser($userId: ID!) {
        enableUser(userId: $userId) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ enableUser: User }>(query, { userId });
    return response.enableUser;
  }

  /**
   * Unlock a locked user (admin).
   *
   * @param userId - User ID
   * @returns Updated user
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async unlockUser(userId: string): Promise<User> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation UnlockUser($userId: ID!) {
        unlockUser(userId: $userId) {
          userId email status statusReason
        }
      }
    `;

    const response = await this.graphqlRequest<{ unlockUser: User }>(query, { userId });
    return response.unlockUser;
  }

  // ============================================
  // Pool Settings (admin)
  // ============================================

  /**
   * Get pool settings (admin).
   *
   * @returns Pool settings
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async getPoolSettings(): Promise<PoolSettings> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      query GetPoolSettings {
        poolSettings {
          passwordMinLength
          passwordRequireUppercase
          passwordRequireLowercase
          passwordRequireNumbers
          passwordRequireSymbols
          sessionDurationMinutes
          sessionRefreshEnabled
          maxConcurrentSessions
          maxFailedLoginAttempts
          lockoutDurationMinutes
          allowSelfRegistration
          requireEmailVerification
        }
      }
    `;

    const response = await this.graphqlRequest<{ poolSettings: PoolSettings }>(query);
    return response.poolSettings;
  }

  /**
   * Update pool settings (admin).
   *
   * @param input - Settings update input
   * @returns Updated pool settings
   * @throws {ErrorAuthentication} If not authenticated
   * @throws {ErrorAuthorization} If missing required scope
   */
  async updatePoolSettings(input: UpdatePoolSettingsInput): Promise<PoolSettings> {
    if (!this.apiKey) {
      throw new ErrorAuthentication("API key required for this operation");
    }

    const query = `
      mutation UpdatePoolSettings($input: UpdatePoolSettingsInput!) {
        updatePoolSettings(input: $input) {
          passwordMinLength
          passwordRequireUppercase
          passwordRequireLowercase
          passwordRequireNumbers
          passwordRequireSymbols
          sessionDurationMinutes
          sessionRefreshEnabled
          maxConcurrentSessions
          maxFailedLoginAttempts
          lockoutDurationMinutes
          allowSelfRegistration
          requireEmailVerification
        }
      }
    `;

    const response = await this.graphqlRequest<{ updatePoolSettings: PoolSettings }>(query, {
      input,
    });

    return response.updatePoolSettings;
  }

  // ============================================
  // Private Methods
  // ============================================

  /**
   * Makes a GraphQL request with retry logic.
   *
   * @param query - The GraphQL query or mutation
   * @param variables - Optional variables for the query
   * @returns The GraphQL response data
   * @throws {Error} If the request fails or returns GraphQL errors
   */
  private async graphqlRequest<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    return this.retryManager.execute(
      () => this.executeGraphQL<T>(query, variables),
      (error) => this.isRetryableError(error),
    );
  }

  /**
   * Executes a single GraphQL request.
   */
  private async executeGraphQL<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (this.apiKey) {
      headers["x-api-key"] = this.apiKey;
    }

    let response: Response;

    try {
      response = await fetch(this.endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          query,
          variables,
        }),
      });
    } catch (error) {
      // Network errors
      throw new NetworkError(error instanceof Error ? error.message : "Network request failed");
    }

    // Handle HTTP 5xx server errors before parsing
    if (response.status >= 500) {
      throw new HttpServerError(`Server error: ${response.status}`, response.status);
    }

    // Parse the response body - GraphQL servers return errors in the body
    // even for 4xx responses, so we parse first then check for GraphQL errors
    const result = (await response.json()) as GraphQLResponse<T>;

    if (result.errors) {
      const error = result.errors[0];

      if (error.extensions?.code === "UNAUTHENTICATED") {
        throw new ErrorAuthentication(error.message);
      }

      if (error.extensions?.code === "FORBIDDEN") {
        throw new ErrorAuthorization(error.message, error.extensions?.requiredScopes || ["userpool:admin"]);
      }

      throw new Error(error.message);
    }

    if (!result.data) {
      throw new Error("GraphQL response missing data field");
    }

    return result.data;
  }

  /**
   * Determines if an error should trigger a retry.
   * Only network/5xx errors are retried, NOT auth failures (4xx).
   */
  private isRetryableError(error: unknown): boolean {
    // Retry network and server errors
    if (error instanceof NetworkError || error instanceof HttpServerError) {
      return true;
    }

    // Don't retry client errors (auth failures, validation, etc.)
    if (error instanceof ErrorAuthentication || error instanceof ErrorAuthorization) {
      return false;
    }

    return false;
  }
}
